import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FileText, User, Mail, Phone, Calendar, Users, Download, SendHorizonal, Loader2, Trash2, X, FileSignature } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { style: 'bg-blue-100 text-blue-800 border border-blue-300', label: 'Nouveau' },
  contacted: { style: 'bg-yellow-100 text-yellow-800 border border-yellow-300', label: 'Contacte' },
  quoted: { style: 'bg-purple-100 text-purple-800 border border-purple-300', label: 'Devis envoye' },
  booked: { style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Reserve' },
  closed: { style: 'bg-gray-100 text-gray-700 border border-gray-300', label: 'Ferme' },
};

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sendingPdf, setSendingPdf] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchQuotes(); }, [token]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(res.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Statut mis a jour');
      fetchQuotes();
    } catch { toast.error('Erreur lors de la mise a jour'); }
  };

  const handleDelete = async (quoteId) => {
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demande supprimee');
      setConfirmDelete(null);
      fetchQuotes();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const createDevisFromQuote = (quote) => {
    const params = new URLSearchParams({
      from_quote: quote.id,
      client_name: quote.customer_name || '',
      client_email: quote.customer_email || '',
      client_phone: quote.customer_phone || '',
      cruise_name: quote.cruise_name || '',
      type: quote.request_type === 'privatization' ? 'privatization' : 'cabin',
      passenger_count: quote.passenger_count || 2,
    });
    navigate(`/admin/devis?${params.toString()}`);
  };

  const downloadPdf = async (quoteId, customerName) => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${customerName.replace(/\s/g, '_')}_SOGNUDIMARE.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Devis PDF telecharge');
    } catch { toast.error('Erreur lors du telechargement du PDF'); }
  };

  const sendPdfByEmail = async (quoteId) => {
    setSendingPdf(quoteId);
    try {
      const res = await axios.post(`${API}/quotes/${quoteId}/send-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message || 'Devis envoye par email');
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setSendingPdf(null); }
  };

  const filteredQuotes = quotes.filter(q => filter === 'all' || q.status === filter);

  const stats = {
    total: quotes.length,
    new: quotes.filter(q => q.status === 'new').length,
    contacted: quotes.filter(q => q.status === 'contacted').length,
    booked: quotes.filter(q => q.status === 'booked').length,
  };

  return (
    <AdminLayout title="Demandes de devis">
      <div className="space-y-6" data-testid="admin-quotes">
        {/* Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">Total: {stats.total}</div>
          <div className="px-3 py-1.5 rounded-full bg-blue-100 text-sm font-medium text-blue-700">Nouveaux: {stats.new}</div>
          <div className="px-3 py-1.5 rounded-full bg-yellow-100 text-sm font-medium text-yellow-700">Contactes: {stats.contacted}</div>
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">Reserves: {stats.booked}</div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[#4A5568]">{filteredQuotes.length} demande{filteredQuotes.length > 1 ? 's' : ''}</p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="quote-filter-select">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="new">Nouvelles</SelectItem>
              <SelectItem value="contacted">Contactees</SelectItem>
              <SelectItem value="quoted">Devis envoye</SelectItem>
              <SelectItem value="booked">Reservees</SelectItem>
              <SelectItem value="closed">Fermees</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}</div>
        ) : filteredQuotes.length > 0 ? (
          <div className="space-y-4">
            {filteredQuotes.map(quote => (
              <Card key={quote.id} className={quote.status === 'new' ? 'border-l-4 border-l-blue-500' : ''} data-testid={`quote-card-${quote.id}`}>
                <CardContent className="p-3 sm:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center shrink-0">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-[#0A2540] truncate">
                            {quote.cruise_name || 'Demande personnalisee'}
                            {quote.quote_number && <span className="ml-2 text-sm font-normal text-[#C4724F]">({quote.quote_number})</span>}
                          </h3>
                          <p className="text-xs text-[#4A5568]">
                            {quote.request_type === 'cabin' ? 'Cabine' : quote.request_type === 'privatization' ? 'Privatisation' : 'Personnalisee'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-[#4A5568] truncate"><User className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{quote.customer_name}</span></div>
                        <div className="flex items-center gap-2 text-[#4A5568] truncate"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{quote.customer_email}</span></div>
                        <div className="flex items-center gap-2 text-[#4A5568]"><Phone className="h-3.5 w-3.5 shrink-0" />{quote.customer_phone}</div>
                        <div className="flex items-center gap-2 text-[#4A5568]"><Users className="h-3.5 w-3.5 shrink-0" />{quote.passenger_count} passagers</div>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        Dates souhaitees: {quote.preferred_dates}
                      </div>

                      {quote.message && (
                        <p className="text-sm text-[#4A5568] bg-[#F8F9FA] p-3 rounded-lg">{quote.message}</p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={statusConfig[quote.status]?.style || 'bg-gray-100 text-gray-700'}>
                        {statusConfig[quote.status]?.label || quote.status}
                      </Badge>

                      <Select value={quote.status} onValueChange={v => updateQuoteStatus(quote.id, v)}>
                        <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* PDF */}
                      <div className="flex gap-1.5 flex-wrap justify-end">
                        <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-7 text-xs" onClick={() => createDevisFromQuote(quote)} data-testid={`create-devis-${quote.id}`}>
                          <FileSignature className="h-3.5 w-3.5 mr-1" />Creer un devis
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadPdf(quote.id, quote.customer_name)} data-testid={`download-pdf-${quote.id}`}>
                          <Download className="h-3.5 w-3.5 mr-1" />PDF
                        </Button>
                        <Button size="sm" className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white h-7 text-xs" onClick={() => sendPdfByEmail(quote.id)} disabled={sendingPdf === quote.id} data-testid={`send-pdf-${quote.id}`}>
                          {sendingPdf === quote.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <SendHorizonal className="h-3.5 w-3.5 mr-1" />}
                          Envoyer
                        </Button>
                      </div>

                      {/* Contact + Delete */}
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                          <a href={`mailto:${quote.customer_email}`}><Mail className="h-3.5 w-3.5 mr-1" />Email</a>
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                          <a href={`tel:${quote.customer_phone}`}><Phone className="h-3.5 w-3.5 mr-1" />Appeler</a>
                        </Button>
                        {confirmDelete === quote.id ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(quote.id)} data-testid={`confirm-delete-quote-${quote.id}`}>
                              Supprimer
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDelete(null)}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => setConfirmDelete(quote.id)} data-testid={`delete-quote-${quote.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#4A5568]">Aucune demande de devis pour le moment</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminQuotes;
